// Shared XHR upload helper. Uses xhr (not fetch) so we can surface real
// upload-progress events to a UI affordance — the Fetch API doesn't expose
// upload progress on Chromium today. Both the avatar uploader and the post-
// media uploader call this — keeping one util means the upload-error wording
// and progress shape are the same everywhere.

export type UploadHeaders = Record<string, string>;

export interface UploadWithProgressArgs {
  readonly url: string;
  readonly body: Blob;
  readonly headers: UploadHeaders;
  readonly onProgress: (pct: number) => void;
  readonly signal?: AbortSignal;
}

export function uploadWithProgress(args: UploadWithProgressArgs): Promise<void> {
  const { url, body, headers, onProgress, signal } = args;
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    // x-amz-meta-* and Content-Type are set as request headers; the signing
    // routine on the server included them in the signature. Browsers ignore
    // forbidden names (Host, Content-Length) rather than throwing, so a throw
    // here is a malformed header — the PUT could never match its signature.
    for (const [k, v] of Object.entries(headers)) {
      xhr.setRequestHeader(k, v);
    }
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) onProgress(Math.round((ev.loaded / ev.total) * 100));
    };
    xhr.onerror = () => reject(new Error('Network error during upload.'));
    xhr.onabort = () => reject(new Error('Upload cancelled.'));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status}).`));
    };
    if (signal !== undefined) {
      // abort() before send() fires no abort event, so settle here instead.
      if (signal.aborted) {
        reject(new Error('Upload cancelled.'));
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(body);
  });
}

/**
 * Simple FIFO semaphore for concurrent uploads. Construct with the desired
 * concurrency cap, then `await semaphore.run(() => upload())` for each task.
 * The 4th task waits until one of the first three finishes.
 */
export class UploadSemaphore {
  private readonly cap: number;
  private inFlight = 0;
  private readonly queue: Array<() => void> = [];

  constructor(cap: number) {
    this.cap = Math.max(1, cap);
  }

  async run<T>(fn: () => Promise<T>): Promise<T> {
    await this.acquire();
    try {
      return await fn();
    } finally {
      this.release();
    }
  }

  private acquire(): Promise<void> {
    if (this.inFlight < this.cap) {
      this.inFlight += 1;
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      this.queue.push(() => {
        this.inFlight += 1;
        resolve();
      });
    });
  }

  private release(): void {
    this.inFlight -= 1;
    const next = this.queue.shift();
    if (next !== undefined) next();
  }
}
